import Vue from 'vue';
import {HTTP} from '@/utils/httpCommonUtil';
import VueSimpleSpinner from 'vue-simple-spinner';
import { BallPulseLoader } from 'vue-loaders';
import {getRestParamErrorMessage, getRestResponseErrorMessage} from '@/utils/serverErrorMessageUtil';
import HttpMixin from '@/mixins/HttpMixin';
import GenericModal from '@/componentsReusable/genericModal.vue';
import UploadControl from '@/componentsReusable/UploadControl.vue';
import MaskedInput from 'vue-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

const ATTACHMENT_ENTITY_TYPE = 20;

export default Vue.component('provider-financial-card', {
  name: 'ProviderFinancialCard',
  components: {
    UploadControl,
    VueSimpleSpinner,
    BallPulseLoader,
    GenericModal,
    MaskedInput
  },
  mixins: [ HttpMixin ],
  filters: {
    secureFilter (toSecure: string) {
      if (toSecure) {
        if (toSecure.length > 4) {
          return '*'.repeat(toSecure.length - 4) + toSecure.substring(toSecure.length - 4);
        } else {
          return toSecure;
        }
      } else {
        return '';
      }
    }
  },
  data () {
    return {
      cardExpanded: false,
      saving: false,
      financialInfos: [],
      errors: {
        quickbooksNameErrorMessage: '',
        quickbooksCompanyNameErrorMessage: '',
        accountTypeIdErrorMessage: '',
        routingNumberErrorMessage: '',
        accountNumberErrorMessage: '',
        w9AcquiredErrorMessage: ''
      },
      showConfirmationModal: false,
      pendingDelete: null,
      accountMask: createNumberMask({
        prefix: '',
        allowDecimal: false,
        includeThousandsSeparator: false,
        integerLimit: 20,
        allowLeadingZeroes: true
      }),
      routingMask: createNumberMask({
        prefix: '',
        allowDecimal: false,
        includeThousandsSeparator: false,
        integerLimit: 9,
        allowLeadingZeroes: true
      }),
    };
  },
  computed: {
    providerId(): string {
      return this.$route.params.providerId;
    },
    shouldWarn (): boolean {
      return this.financialInfos.length > 0 &&
        this.financialInfos.some((info: any) => !info.quickbooksName || !info.quickbooksCompanyName || !info.routingNumber || !info.accountNumber || !info.w9Acquired);
    }
  },
  async created () {
    await this.expandCard();
  },
  methods: {
    async expandCard() {
      this.cardExpanded = !this.cardExpanded;
      if (this.cardExpanded) {
        await this.loadData();
      }
    },
    async loadData() {
      const data = await (this as any).httpGet(`/providers/${this.providerId}/financial`, 'Error fetching Financial Information');
      this.financialInfos = data.map((info: any) => { info.isEditing = false; return info }) || [];
      this.clearErrors();
    },
    clearErrors() {
      this.errors.quickbooksNameErrorMessage = '';
      this.errors.quickbooksCompanyNameErrorMessage = '';
      this.errors.accountTypeIdErrorMessage = '';
      this.errors.routingNumberErrorMessage = '';
      this.errors.accountNumberErrorMessage = '';
      this.errors.w9AcquiredErrorMessage = '';
    },
    async save(financialInfo: any) {
      try {
        this.clearErrors();
        this.saving = true;
        await HTTP.put(`/api/providers/${this.providerId}/financial`, financialInfo);
        this.saving = false;
      } catch (err) {
        this.saving = false;
        this.errors.quickbooksNameErrorMessage = getRestParamErrorMessage(err, 'quickbooksName');
        this.errors.quickbooksCompanyNameErrorMessage = getRestParamErrorMessage(err, 'quickbooksCompanyName');
        this.errors.accountTypeIdErrorMessage = getRestParamErrorMessage(err, 'accountTypeId');
        this.errors.routingNumberErrorMessage = getRestParamErrorMessage(err, 'routingNumber');
        this.errors.accountNumberErrorMessage = getRestParamErrorMessage(err, 'accountNumber');
        this.errors.w9AcquiredErrorMessage = getRestParamErrorMessage(err, 'w9Acquired');
        if (!this.errors.quickbooksNameErrorMessage &&
            !this.errors.quickbooksCompanyNameErrorMessage &&
            !this.errors.accountTypeIdErrorMessage &&
            !this.errors.routingNumberErrorMessage &&
            !this.errors.accountNumberErrorMessage &&
            !this.errors.w9AcquiredErrorMessage) {
          let generalError = getRestResponseErrorMessage(err);
          this.$notify({
            group: 'main',
            type: 'error',
            title: 'Error Saving',
            text: generalError ? generalError : 'An error occurred saving Financial information.',
          });
        }
      }
    },
    async cancel(financialInfo: any) {
      await this.loadData();
      financialInfo.isEditing = false;
    },
    openForEdit (financialInfo: any) {
      financialInfo.isEditing = !financialInfo.isEditing;
    },
    async associateUploadedFile (s3FilePath: string, certificationId: any, fileName: string, financialInfo: any) {
      try {
        const response = await HTTP.post('/api/attachment/', {
          "entityId": certificationId,
          "entityType": ATTACHMENT_ENTITY_TYPE,
          "attachmentName": fileName,
          "s3Key": s3FilePath
        });
        if (!response.data || !response.data.success) {
          throw new Error("Error associating uploaded file.");
        }
        financialInfo.attachments.push({
          id: response.data.data.attachmentId,
          name: fileName,
          s3Key: s3FilePath
        });
      } catch (err) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred saving uploaded W9.'
        });
      }
    },
    async openAttachment (s3Key: string) {
      try {
        const response = await HTTP.get('/api/s3/signed-url/get-secure?s3key=' + s3Key)
        if (!response.data || !response.data.data) {
          throw new Error("Error getting attachment.");
        }
        window.open(response.data.data, '_blank');
      } catch (err) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred saving uploaded file attachment.'
        });
      }
    },
    async deleteConfirmed () {
      this.showConfirmationModal = false;
      await this.deleteAttachment(this.pendingDelete, true);
    },
    async deleteAttachment (id: any, confirmed: boolean = false) {
      if (!confirmed) {
        this.showConfirmationModal = true;
        this.pendingDelete = id;
        return;
      } else {
        this.pendingDelete = null;
      }
      await (this as any).httpDelete(`/providers/${this.providerId}/financial/attachments/${id}`, 'An error occurred deleting W9.');
      await this.loadData();
    }
  }
});



// WEBPACK FOOTER //
// ./src/components/provider/cards/financial/ProviderFinancialCard.ts